import * as React from "react";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import { Icon } from "@utdanningsdirektoratet/icon";
import { useId, uuid } from "@utdanningsdirektoratet/utils/useId";
import { add, remove } from "@utdanningsdirektoratet/utils/array";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import Suggestions from "./Suggestions";
import Tag from "./Tag";
import Keys from "./keys";
import { SuggestionType, TagType } from "./types";
import styles from "./TagsInput.module.less";

declare global {
  interface Window {
    clipboardData: DataTransfer;
  }
}

type TagsInputProps = {
  value: Array<TagType>;
  suggestions?: Array<SuggestionType>;
  id?: string;
  label?: React.ReactNode | string;
  "aria-label"?: string;
  delimiters?: Array<(typeof Keys)[keyof typeof Keys]>;
  pasteSplitRegex?: RegExp | null;
  placeholder?: string;
  allowDelete?: boolean;
  readOnly?: boolean;
  onChange: (tags: Array<TagType>) => void;
  onTextChange?: (query: string) => void;
  validationRegex?: RegExp | ((tag: TagType) => TagType | null);
  placement?: "top" | "bottom";
  message?: React.ReactNode | string;
  tagmessage?: React.ReactNode | string;
  error?: boolean;
  fullwidth?: boolean;
  uniqueOnly?: boolean;
  maximumNumbersOfTags?: number;
  suggestionsOnly?: boolean;
  suggestionMaxNum?: number;
  suggestionMinQueryLength?: number;
  suggestionScrollable?: number;
  className?: string;
  addOnBlur?: boolean;
  sensitive?: boolean;
  tagColor?: "none" | "offwhite";
};

const TagsInput: React.FC<TagsInputProps> = (props) => {
  const id = useId(props.id);
  const [tags, setTags] = React.useState<Array<TagType>>(props.value || []);
  const [suggestions, setSuggestions] = React.useState<Array<SuggestionType>>(
    [],
  );
  const [query, setQuery] = React.useState("");
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [selectionMode, setSelectionMode] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [error, setError] = React.useState(props.error || false);
  const [shouldScrollToSuggestion, setShouldScrollToSuggestion] =
    React.useState(false);

  const ref = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const suggestionRef = React.useRef<HTMLUListElement>(null);

  const {
    addOnBlur,
    suggestionsOnly,
    delimiters = [Keys.ENTER, Keys.TAB],
    allowDelete,
    value,
    uniqueOnly,
    label,
    onTextChange,
    message,
    tagmessage,
    readOnly,
    suggestionMinQueryLength = 1,
    validationRegex,
    suggestionMaxNum = 20,
    maximumNumbersOfTags,
    placeholder,
    className,
    fullwidth,
    suggestionScrollable,
    sensitive,
    placement = "top",
    pasteSplitRegex = / /g,
    tagColor = "none",
  } = props;

  const onChange = (e: Array<TagType>) => {
    props.onChange(e);
  };

  const validateTag = (tag: TagType): TagType | null => {
    if (typeof validationRegex === "function") return validationRegex(tag);
    if (typeof validationRegex === "object")
      return validationRegex.test(tag.title) ? tag : null;
    return null;
  };

  const filteredSuggestions = (
    _query: string,
    _suggestions: Array<SuggestionType> = [],
  ): Array<SuggestionType> => {
    return _suggestions
      .filter((item) =>
        (value || []).every(
          (m) =>
            item.id !== m.id ||
            item.title.toLowerCase().indexOf(m.title.toLowerCase()) === -1,
        ),
      )
      .filter(
        (item) => item.title.toLowerCase().indexOf(_query.toLowerCase()) !== -1,
      )
      .slice(0, suggestionMaxNum);
  };

  const filteredTags = (_tags: Array<TagType>): Array<TagType> => {
    if (!validationRegex) return _tags;
    return _tags.filter((t) => validateTag(t));
  };

  const handleDelete = (index: number): void => {
    const _tags = remove(tags, index);
    onChange(filteredTags(_tags));
    setTags(_tags);
    if (ref.current) ref.current.focus();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const _query = event.target.value.trim();
    const _suggestions = filteredSuggestions(_query, props.suggestions);
    setQuery(_query);
    setSuggestions(_suggestions);
    setSelectedIndex(-1);
    setSelectionMode(false);
    setError((prevState) => props.error || (_query.length > 0 && prevState));
    if (onTextChange) onTextChange(_query);
  };

  const handleFocus = (): void => {
    setIsFocused(true);
  };

  const addTag = (
    tag: string | TagType | SuggestionType,
    refocus = true,
    multiple = false,
    _tags: Array<TagType> = tags,
  ): Array<TagType> => {
    const tagObject: TagType =
      typeof tag !== "object"
        ? {
            id: uuid(),
            title: tag,
          }
        : (tag as TagType);

    const _addTag = (_tag: TagType): Array<TagType> => {
      let _newTags = _tags;
      if (
        !(
          uniqueOnly &&
          _tags.filter(
            (t) => t.title.toLowerCase() === _tag.title.toLowerCase(),
          ).length > 0
        )
      ) {
        _newTags = add(_newTags, _tag);
        if (!multiple) onChange(filteredTags(_newTags));
        setTags(_newTags);
      }
      setQuery("");
      setSelectedIndex(-1);
      setSelectionMode(false);
      if (ref.current) ref.current.value = "";
      if (ref.current && refocus) ref.current.focus();
      return _newTags;
    };

    if (validationRegex) {
      const validatedTag = validateTag(tagObject);
      if (validatedTag) {
        setError(props.error || false);
        return _addTag(validatedTag);
      }
      setError(true);
      if (multiple) {
        return _addTag({ ...tagObject, error: true });
      }
      return tags;
    }
    return _addTag(tagObject);
  };

  const addTags = (
    _tags: Array<string>,
    refocus = true,
    multiple = false,
  ): void => {
    let existingTags = tags;
    _tags.forEach((tag) => {
      existingTags = addTag(tag, refocus, multiple, existingTags);
    });
    if (multiple) onChange(filteredTags(existingTags));
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const _query = event.target.value.trim();
    const _suggestions = filteredSuggestions(_query, props.suggestions);
    if (_query !== "" && addOnBlur) {
      if (selectionMode) {
        addTag(suggestions[selectedIndex], false);
      } else if (!suggestionsOnly) {
        addTag(_query, false);
      }
      setIsFocused(false);
    } else {
      setSuggestions(_suggestions);
      setSelectedIndex(-1);
      setSelectionMode(false);
      setError((prevState) => props.error || (_query.length > 0 && prevState));
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === Keys.ENTER) {
      event.preventDefault();
    }

    setError(props.error || false);

    if (event.key === Keys.ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
      setSelectedIndex(-1);
      setSelectionMode(false);
    }

    if (delimiters.indexOf(event.key as any) !== -1) {
      if (!error && (event.key !== Keys.TAB || query !== "")) {
        event.preventDefault();
      }

      if (query !== "") {
        if (selectionMode) {
          addTag(suggestions[selectedIndex]);
        } else if (!suggestionsOnly) {
          addTag(query);
        }
      }
    }

    if (event.key === Keys.BACKSPACE && query === "" && allowDelete) {
      handleDelete(tags.length - 1);
    }

    if (event.key === Keys.UP_ARROW) {
      event.preventDefault();
      if (selectedIndex <= 0) {
        setSelectedIndex(suggestions.length - 1);
        setSelectionMode(true);
        setShouldScrollToSuggestion(true);
      } else {
        setSelectedIndex((prevState) => prevState - 1);
        setSelectionMode(true);
        setShouldScrollToSuggestion(true);
      }
    }

    if (event.key === Keys.DOWN_ARROW) {
      event.preventDefault();
      setSelectedIndex((prevState) => (prevState + 1) % suggestions.length);
      setSelectionMode(true);
      setShouldScrollToSuggestion(true);
    }
  };

  const scrollToSuggestion = (index: number): void => {
    if (suggestionRef.current && suggestionRef.current.childNodes[index]) {
      (suggestionRef.current.childNodes[index] as HTMLLIElement).scrollIntoView(
        {
          behavior: "auto",
          block: "nearest",
          inline: "nearest",
        },
      );
    }
  };

  React.useEffect(() => {
    setTags(value);
  }, [value]);

  React.useEffect(() => {
    if (shouldScrollToSuggestion) {
      scrollToSuggestion(selectedIndex);
      setShouldScrollToSuggestion(false);
    }
  }, [shouldScrollToSuggestion, selectedIndex]);

  React.useEffect(() => {
    if (suggestions !== props.suggestions) {
      setSuggestions(filteredSuggestions(query, props.suggestions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, props.suggestions]);

  React.useEffect(() => {
    if (props.error && props.error !== error) setError(props.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.error]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Element)) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsFocused, isFocused]);

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
    const clipboardData = event.clipboardData || window.clipboardData;
    const string = clipboardData.getData("text");
    const _tags = (pasteSplitRegex ? string.split(pasteSplitRegex) : [string])
      .map((tag: string) => tag.replace(/^\s+|\s+$/g, ""))
      .filter((tag: string) => tag);
    addTags(_tags, true, true);
  };

  const handleSuggestionClick = (index: number): void => {
    addTag(suggestions[index]);
  };

  const handleSuggestionHover = (index: number): void => {
    setSelectedIndex(index);
    setSelectionMode(true);
  };

  const isEmpty = query.length === 0 && (tags.length === 0 || !isFocused);

  const inputClassName = cls({
    [styles.tagsInputInput]: true,
    [styles.tagsInputInputEmpty]: isEmpty,
    [styles.tagsInputInputError]: error,
  });

  const iconClass = cls({
    [styles.tagsInputIcon]: true,
    [styles.tagsInputIconVisible]: error,
  });

  const labelClass = cls({
    [styles.tagsInputLabel]: true,
    [styles.tagsInputLabelFocused]: isFocused,
  });

  const input = (
    <div ref={containerRef} className={styles.tagsInputWrapper}>
      <Tooltip
        className={styles.tagsInputTooltip}
        message={message}
        disabled={!error}
        alwaysShow={error}
        align="right"
        alert
      >
        {({ ref: tooltipRef, ...rest }) => (
          <span ref={tooltipRef}>
            <input
              {...rest}
              ref={ref}
              className={inputClassName}
              type="text"
              id={id}
              aria-invalid={error}
              placeholder={placeholder}
              aria-label={props["aria-label"]}
              aria-labelledby={label ? `${id}-label` : undefined}
              aria-haspopup={suggestions.length > 0 ? "listbox" : undefined}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={
                !!maximumNumbersOfTags && tags.length >= maximumNumbersOfTags
              }
              onPaste={handlePaste}
            />
            <Icon
              className={iconClass}
              color="darkred"
              aria-hidden={error ? "false" : "true"}
              icon="warning"
            />
          </span>
        )}
      </Tooltip>
      <Suggestions
        query={query}
        ref={suggestionRef}
        suggestions={suggestions}
        selectedIndex={selectedIndex}
        handleClick={handleSuggestionClick}
        handleHover={handleSuggestionHover}
        scrollable={suggestionScrollable}
        isFocused={isFocused}
        minQueryLength={suggestionMinQueryLength}
      />
    </div>
  );

  return (
    <div className={cls(styles.tagsInputTags, className)}>
      <label htmlFor={id} id={`${id}-label`} className={labelClass}>
        {label}
        {!readOnly && placement === "bottom" ? input : null}
        <div>
          {tags
            .sort((a, b) => (a.error ? 1 : 0) - (b.error ? 1 : 0))
            .map((tag, i) => {
              return (
                <Tag
                  key={i}
                  error={tag.error}
                  tag={tag}
                  message={tagmessage || message}
                  onDelete={() => handleDelete(i)}
                  readOnly={readOnly}
                  fullwidth={fullwidth}
                  sensitive={sensitive}
                  color={tagColor}
                />
              );
            })}
        </div>
        {!readOnly && placement === "top" ? input : null}
      </label>
    </div>
  );
};

export default TagsInput;
